import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
const API_BASE = "https://schoolcrm-fzcv.onrender.com/api";

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────
function initials(name) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase();
}
function buildAdvisorClassName(cls, group, section) {
  // Matches the format used by StaffManagement.js / classAdvisorRoutes.js:
  //   "Class 9-A"                     (regular classes)
  //   "Class 11 Bio Maths-A"          (Higher Secondary groups)
  if (group) return `${cls} ${group}-${section}`;
  return `${cls}-${section}`;
}
function Card({
  icon,
  title,
  children,
  right
}) {
  return <div style={{
    background: "#FFFFFF",
    borderRadius: 18,
    padding: 18,
    boxShadow: "0 1px 3px rgba(13,71,161,0.06)",
    marginBottom: 16
  }}>
      <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 14
    }}>
        <div style={{
        fontSize: 13,
        fontWeight: 700,
        color: "#0D47A1",
        display: "flex",
        alignItems: "center",
        gap: 8,
        textTransform: "uppercase",
        letterSpacing: "0.4px"
      }}>
          <i className={`fas ${icon}`}></i> {title}
        </div>
        {right}
      </div>
      {children}
    </div>;
}
function InfoRow({
  label,
  value
}) {
  return <div style={{
    background: "#E3F2FD",
    borderRadius: 10,
    padding: "9px 12px"
  }}>
      <div style={{
      fontSize: 10,
      color: "#2196F3",
      textTransform: "uppercase",
      letterSpacing: "0.4px"
    }}>{label}</div>
      <div style={{
      fontSize: 13,
      fontWeight: 600,
      color: "#0D47A1",
      marginTop: 2,
      wordBreak: "break-word"
    }}>{value || "—"}</div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Performance donut chart (pure SVG, no external chart library needed)
// ─────────────────────────────────────────────────────────────────────────────
function PerformanceDonut({
  percentage
}) {
  const pct = Math.max(0, Math.min(100, Math.round(percentage)));
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const filled = pct / 100 * circumference;
  const color = pct >= 75 ? "#0D47A1" : pct >= 50 ? "#2196F3" : "#90CAF9";
  return <div style={{
    display: "flex",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap"
  }}>
      <svg width="120" height="120" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={radius} fill="none" stroke="#E3F2FD" strokeWidth="14" />
        <circle cx="60" cy="60" r={radius} fill="none" stroke={color} strokeWidth="14" strokeDasharray={`${filled} ${circumference - filled}`} strokeLinecap="round" transform="rotate(-90 60 60)" />
        <text x="60" y="56" textAnchor="middle" fontSize="22" fontWeight="800" fill="#0D47A1">{pct}%</text>
        <text x="60" y="74" textAnchor="middle" fontSize="9" fill="#2196F3">AVERAGE</text>
      </svg>
      <div style={{
      fontSize: 12,
      color: "#0D47A1",
      maxWidth: 220,
      lineHeight: 1.6
    }}>
        Overall average score across all recorded exams and subjects.
        {pct >= 75 ? " Performing well." : pct >= 50 ? " Doing okay — room to improve." : pct > 0 ? " Needs attention." : " No marks recorded yet."}
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function StudentAnalysis() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cls = searchParams.get("cls") || "";
  const section = searchParams.get("section") || "";
  const group = searchParams.get("group") || "";
  const [loadingList, setLoadingList] = useState(true);
  const [students, setStudents] = useState([]); // filtered to this class+section
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [admission, setAdmission] = useState(null);
  const [marks, setMarks] = useState([]);
  const [advisor, setAdvisor] = useState(null);
  const [classStaff, setClassStaff] = useState([]);
  const [feeHistory, setFeeHistory] = useState([]);
  const [hostelInfo, setHostelInfo] = useState({
    isHosteller: false
  });
  const [hostelRequests, setHostelRequests] = useState([]);

  // ── Load the class+section student list for the sidebar ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingList(true);
      try {
        const r = await fetch(`${API_BASE}/admissions`);
        const data = r.ok ? await r.json() : [];
        const list = (Array.isArray(data) ? data : []).filter(a => {
          if ((a.applyingClass || "") !== cls) return false;
          if ((a.section || "") !== section) return false;
          if (group && (a.group || "") !== group) return false;
          return true;
        });
        if (cancelled) return;
        setStudents(list);
        if (list.length > 0) setSelectedId(list[0]._id);
      } catch (err) {
        console.error("StudentAnalysis list load error:", err);
        if (!cancelled) setStudents([]);
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cls, section, group]);

  // ── Load full detail for the selected student ──
  useEffect(() => {
    if (!selectedId) {
      setAdmission(null);
      return;
    }
    let cancelled = false;
    setClassStaff([]);
    (async () => {
      setLoadingDetail(true);
      try {
        const admRes = await fetch(`${API_BASE}/admissions/${selectedId}`);
        const adm = admRes.ok ? await admRes.json() : null;
        if (cancelled) return;
        setAdmission(adm);
        if (adm) {
          const advisorClassName = buildAdvisorClassName(adm.applyingClass || "", adm.group || "", adm.section || "");
          fetch(`${API_BASE}/marks/student/${selectedId}`).then(r => r.ok ? r.json() : []).then(data => {
            if (!cancelled) setMarks(Array.isArray(data) ? data : []);
          }).catch(() => {
            if (!cancelled) setMarks([]);
          });
          fetch(`${API_BASE}/class-advisors/${encodeURIComponent(advisorClassName)}`).then(r => r.ok ? r.json() : null).then(data => {
            if (!cancelled) setAdvisor(data);
          }).catch(() => {
            if (!cancelled) setAdvisor(null);
          });

          // Class staff — every subject teacher assigned to this class+section's timetable
          fetch(`${API_BASE}/timetables/${encodeURIComponent(advisorClassName)}`).then(r => r.ok ? r.json() : null).then(data => {
            if (cancelled) return;
            const map = {};
            const tt = data?.timetable || {};
            Object.values(tt).forEach(day => {
              if (!day || typeof day !== "object") return;
              Object.values(day).forEach(cell => {
                const t = (cell?.teacher || "").trim();
                if (!t) return;
                if (!map[t]) map[t] = new Set();
                const subj = (cell?.subject || "").trim();
                if (subj) map[t].add(subj);
              });
            });
            setClassStaff(Object.entries(map).map(([name, subs]) => ({
              name,
              subjects: Array.from(subs)
            })));
          }).catch(() => {
            if (!cancelled) setClassStaff([]);
          });
          fetch(`${API_BASE}/fees/student/${selectedId}`).then(r => r.ok ? r.json() : []).then(data => {
            if (!cancelled) setFeeHistory(Array.isArray(data) ? data : []);
          }).catch(() => {
            if (!cancelled) setFeeHistory([]);
          });
          fetch(`${API_BASE}/hostel/student-by-admission/${selectedId}`).then(r => r.ok ? r.json() : {
            isHosteller: false
          }).then(data => {
            if (cancelled) return;
            setHostelInfo(data);
            setHostelRequests([]);
            if (data.isHosteller && data.hostelStudent?._id) {
              fetch(`${API_BASE}/hostel/requests-by-student/${data.hostelStudent._id}`).then(r => r.ok ? r.json() : []).then(reqs => {
                if (!cancelled) setHostelRequests(Array.isArray(reqs) ? reqs : []);
              }).catch(() => {
                if (!cancelled) setHostelRequests([]);
              });
            }
          }).catch(() => {
            if (!cancelled) setHostelInfo({
              isHosteller: false
            });
          });
        }
      } catch (err) {
        console.error("StudentAnalysis detail load error:", err);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);
  const visibleStudents = useMemo(() => {
    if (!search.trim()) return students;
    const q = search.trim().toLowerCase();
    return students.filter(s => (s.studentName || "").toLowerCase().includes(q) || (s.rollNo || "").toLowerCase().includes(q));
  }, [students, search]);

  // ── Derived values for the detail panel ──
  const totalFee = admission?.totalFee || 0;
  const termFees = admission?.termFees || {};
  const paid = ["T1", "T2", "T3"].reduce((s, t) => s + (termFees[t]?.paid || 0), 0);
  const pending = Math.max(0, totalFee - paid);
  const scoredMarks = marks.filter(m => m.entry && typeof m.entry.marks === "number");
  const avgPercentage = scoredMarks.length ? scoredMarks.reduce((s, m) => s + m.entry.marks / (m.entry.maxMarks || 100) * 100, 0) / scoredMarks.length : 0;
  return <div style={{
    minHeight: "100vh",
    background: "#EAF4FE",
    fontFamily: "'Poppins', sans-serif"
  }}>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <style>{`
        @media (max-width: 900px) {
          .sa-page-body { grid-template-columns: 1fr !important; }
          .sa-sidebar { max-height: 320px; }
        }
      `}</style>

      {/* Header */}
      <div style={{
      background: "#0D47A1",
      padding: "18px 24px",
      display: "flex",
      alignItems: "center",
      gap: 14
    }}>
        <button onClick={() => navigate(-1)} style={{
        background: "rgba(255,255,255,0.15)",
        border: "none",
        color: "#FFFFFF",
        width: 40,
        height: 40,
        borderRadius: 12,
        cursor: "pointer",
        fontSize: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
      }}>
          <i className="fas fa-arrow-left"></i>
        </button>
        <div>
          <div style={{
          fontWeight: 800,
          fontSize: 18,
          color: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          gap: 8
        }}>
            <i className="fas fa-chart-line"></i> Student Analysis &amp; Performance
          </div>
          <div style={{
          fontSize: 12,
          color: "#90CAF9",
          marginTop: 2
        }}>
            {cls}{group ? ` · ${group}` : ""} · Section {section} — full profile, fees &amp; performance per student
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="sa-page-body" style={{
      display: "grid",
      gridTemplateColumns: "300px 1fr",
      minHeight: "calc(100vh - 76px)"
    }}>
        {/* Sidebar */}
        <div className="sa-sidebar" style={{
        background: "#FFFFFF",
        borderRight: "1px solid #E3F2FD",
        padding: 16,
        overflowY: "auto"
      }}>
          <div style={{
          position: "relative",
          marginBottom: 14
        }}>
            <i className="fas fa-search" style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#2196F3",
            fontSize: 12
          }}></i>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search student..." style={{
            width: "100%",
            padding: "9px 12px 9px 32px",
            borderRadius: 10,
            border: "1px solid #E3F2FD",
            outline: "none",
            fontSize: 13,
            fontFamily: "'Poppins', sans-serif",
            boxSizing: "border-box"
          }} />
          </div>

          {loadingList ? <div style={{
          textAlign: "center",
          padding: "30px 0",
          color: "#0D47A1"
        }}>
              <i className="fas fa-spinner fa-pulse" style={{
            fontSize: 20
          }}></i>
            </div> : visibleStudents.length === 0 ? <div style={{
          textAlign: "center",
          padding: "30px 0",
          color: "#2196F3",
          fontSize: 12
        }}>
              <i className="fas fa-user-slash" style={{
            fontSize: 22,
            marginBottom: 8,
            display: "block"
          }}></i>
              No students found
            </div> : <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 6
        }}>
              {visibleStudents.map(s => {
            const active = s._id === selectedId;
            return <div key={s._id} onClick={() => setSelectedId(s._id)} style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 10px",
              borderRadius: 12,
              cursor: "pointer",
              background: active ? "#0D47A1" : "transparent",
              transition: "background 0.15s"
            }}>
                    <div style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                flexShrink: 0,
                overflow: "hidden",
                background: active ? "rgba(255,255,255,0.2)" : "#E3F2FD",
                color: active ? "#FFFFFF" : "#0D47A1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 12
              }}>
                      {s.photo ? <img src={s.photo} alt={s.studentName} style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }} /> : initials(s.studentName)}
                    </div>
                    <div style={{
                minWidth: 0
              }}>
                      <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: active ? "#FFFFFF" : "#0D47A1",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>{s.studentName}</div>
                      <div style={{
                  fontSize: 11,
                  color: active ? "#E3F2FD" : "#2196F3"
                }}>{s.rollNo ? `Roll ${s.rollNo}` : "—"}</div>
                    </div>
                  </div>;
          })}
            </div>}
        </div>

        {/* Detail panel */}
        <div style={{
        padding: "20px 24px 40px",
        overflowY: "auto"
      }}>
          {loadingDetail ? <div style={{
          textAlign: "center",
          padding: "60px 0",
          color: "#0D47A1"
        }}>
              <i className="fas fa-spinner fa-pulse" style={{
            fontSize: 28,
            marginBottom: 12
          }}></i>
              <div style={{
            fontSize: 13
          }}>Loading student details...</div>
            </div> : !admission ? <div style={{
          textAlign: "center",
          padding: "60px 0",
          color: "#0D47A1"
        }}>
              <i className="fas fa-user-graduate" style={{
            fontSize: 32,
            marginBottom: 12,
            display: "block"
          }}></i>
              Select a student from the sidebar to view their details.
            </div> : <>
              {/* Student header card */}
              <div style={{
            background: "linear-gradient(135deg, #0D47A1, #2196F3)",
            borderRadius: 20,
            padding: "18px 22px",
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginBottom: 18,
            boxShadow: "0 8px 20px rgba(13,71,161,0.15)"
          }}>
                <div style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 20,
              color: "#FFFFFF",
              overflow: "hidden",
              flexShrink: 0
            }}>
                  {admission.photo ? <img src={admission.photo} alt={admission.studentName} style={{
                width: "100%",
                height: "100%",
                objectFit: "cover"
              }} /> : initials(admission.studentName)}
                </div>
                <div>
                  <div style={{
                fontWeight: 800,
                fontSize: 18,
                color: "#FFFFFF"
              }}>{admission.studentName}</div>
                  <div style={{
                fontSize: 12,
                color: "#E3F2FD",
                marginTop: 3
              }}>
                    {admission.applyingClass}{admission.group ? ` · ${admission.group}` : ""} · Section {admission.section}{admission.rollNo ? ` · Roll ${admission.rollNo}` : ""}
                  </div>
                </div>
              </div>

              <div style={{
            display: "grid",
            gridTemplateColumns: "1.3fr 1fr",
            gap: 16
          }} className="sa-detail-grid">
                <div>
                  {/* Personal & Parents */}
                  <Card icon="fa-id-card" title="Personal & Parent Details">
                    <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 10
                }}>
                      <InfoRow label="Date of Birth" value={admission.dob} />
                      <InfoRow label="Gender" value={admission.gender} />
                      <InfoRow label="Blood Group" value={admission.bloodGroup} />
                      <InfoRow label="Address" value={[admission.address, admission.city, admission.pincode].filter(Boolean).join(", ")} />
                      <InfoRow label="Father's Name" value={admission.fatherName} />
                      <InfoRow label="Father's Phone" value={admission.fatherMobile} />
                      <InfoRow label="Father's Occupation" value={admission.fatherOccupation} />
                      <InfoRow label="Mother's Name" value={admission.motherName} />
                      <InfoRow label="Mother's Phone" value={admission.motherMobile} />
                    </div>
                  </Card>

                  {/* Class, Advisor & Staff */}
                  <Card icon="fa-chalkboard-teacher" title="Class, Advisor & Staff">
                    <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 10
                }}>
                      <InfoRow label="Class" value={`${admission.applyingClass}${admission.group ? ` · ${admission.group}` : ""}`} />
                      <InfoRow label="Section" value={admission.section} />
                      <InfoRow label="Medium" value={admission.medium} />
                      <InfoRow label="Class Advisor" value={advisor?.staffId?.name ? `${advisor.staffId.name}${advisor.staffId.dept ? ` (${advisor.staffId.dept})` : ""}` : "Not assigned"} />
                    </div>
                    {classStaff.length > 0 && <div style={{
                  marginTop: 14
                }}>
                        <div style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#0D47A1",
                    marginBottom: 6,
                    textTransform: "uppercase"
                  }}>Subject Teachers (from Timetable)</div>
                        <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6
                  }}>
                          {classStaff.map(t => <div key={t.name} style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      background: "#E3F2FD",
                      borderRadius: 8,
                      padding: "7px 12px",
                      flexWrap: "wrap"
                    }}>
                              <span style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#0D47A1"
                      }}>{t.name}</span>
                              <span style={{
                        fontSize: 11,
                        color: "#2196F3"
                      }}>{t.subjects.join(", ") || "—"}</span>
                            </div>)}
                        </div>
                      </div>}
                  </Card>

                  {/* Performance */}
                  <Card icon="fa-chart-pie" title="Studies Performance">
                    <PerformanceDonut percentage={avgPercentage} />
                    {scoredMarks.length > 0 && <div style={{
                  marginTop: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6
                }}>
                        {scoredMarks.map(m => <div key={m._id} style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "#E3F2FD",
                    borderRadius: 8,
                    padding: "7px 12px"
                  }}>
                            <span style={{
                      fontSize: 12,
                      color: "#0D47A1",
                      fontWeight: 600
                    }}>{m.subject} <span style={{
                        fontWeight: 400,
                        color: "#2196F3"
                      }}>· {m.exam}</span></span>
                            <span style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#0D47A1"
                    }}>{m.entry.marks} / {m.entry.maxMarks || 100}{m.entry.grade ? ` (${m.entry.grade})` : ""}</span>
                          </div>)}
                      </div>}
                  </Card>
                </div>

                <div>
                  {/* Fees */}
                  <Card icon="fa-file-invoice-dollar" title="Fee Details">
                    <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 10,
                  marginBottom: 12
                }}>
                      <InfoRow label="Total Fee" value={`₹${totalFee.toLocaleString()}`} />
                      <InfoRow label="Paid" value={`₹${paid.toLocaleString()}`} />
                      <InfoRow label="Pending" value={`₹${pending.toLocaleString()}`} />
                    </div>
                    <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8
                }}>
                      {["T1", "T2", "T3"].map(t => {
                    const tf = termFees[t] || {
                      due: 0,
                      paid: 0,
                      pending: 0
                    };
                    return <div key={t} style={{
                      background: "#E3F2FD",
                      borderRadius: 10,
                      padding: "8px 12px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 4
                    }}>
                            <span style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#0D47A1"
                      }}>{t}</span>
                            <span style={{
                        fontSize: 11,
                        color: "#0D47A1"
                      }}>Due ₹{(tf.due || 0).toLocaleString()} · Paid ₹{(tf.paid || 0).toLocaleString()} · Pending ₹{(tf.pending || 0).toLocaleString()}</span>
                          </div>;
                  })}
                    </div>
                    {feeHistory.length > 0 && <div style={{
                  marginTop: 14
                }}>
                        <div style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#0D47A1",
                    marginBottom: 6,
                    textTransform: "uppercase"
                  }}>Recent Payments</div>
                        <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    maxHeight: 160,
                    overflowY: "auto"
                  }}>
                          {feeHistory.slice(0, 6).map(f => <div key={f._id} style={{
                      fontSize: 11,
                      color: "#0D47A1",
                      background: "#E3F2FD",
                      borderRadius: 8,
                      padding: "6px 10px"
                    }}>
                              ₹{(f.amount || 0).toLocaleString()} · {f.term || "—"} · {f.method || "cash"} · {f.createdAt ? new Date(f.createdAt).toLocaleDateString() : ""}
                            </div>)}
                        </div>
                      </div>}
                  </Card>

                  {/* Hostel / Day Scholar */}
                  <Card icon={hostelInfo.isHosteller ? "fa-house-user" : "fa-house"} title={hostelInfo.isHosteller ? "Hostel Details" : "Day Scholar"}>
                    {!hostelInfo.isHosteller ? <div style={{
                  fontSize: 12,
                  color: "#0D47A1",
                  background: "#E3F2FD",
                  borderRadius: 10,
                  padding: "10px 12px"
                }}>
                        This student is a <b>Day Scholar</b> — not registered in the hostel.
                      </div> : <>
                        <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                    gap: 10,
                    marginBottom: hostelRequests.length ? 14 : 0
                  }}>
                          <InfoRow label="Hostel" value={`${hostelInfo.hostelStudent.hostelType} Hostel`} />
                          <InfoRow label="Block" value={hostelInfo.roomInfo?.blockName} />
                          <InfoRow label="Floor" value={hostelInfo.roomInfo?.floorNo} />
                          <InfoRow label="Room No" value={hostelInfo.roomInfo?.roomNo} />
                          <InfoRow label="Parent Contact" value={hostelInfo.hostelStudent.parentNumber} />
                        </div>
                        {hostelRequests.length > 0 && <div>
                            <div style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#0D47A1",
                      marginBottom: 6,
                      textTransform: "uppercase"
                    }}>Leave / Permission History</div>
                            <div style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      maxHeight: 160,
                      overflowY: "auto"
                    }}>
                              {hostelRequests.map(r => <div key={r._id} style={{
                        background: "#E3F2FD",
                        borderRadius: 8,
                        padding: "8px 12px"
                      }}>
                                  <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#0D47A1"
                        }}>
                                    <span>{r.type}{r.type === "Leave" ? ` (${r.fromDate} ${r.toDate})` : ` (${r.date})`}</span>
                                    <span>{r.status}</span>
                                  </div>
                                  {r.reason && <div style={{
                          fontSize: 11,
                          color: "#0D47A1",
                          marginTop: 3
                        }}>{r.reason}</div>}
                                </div>)}
                            </div>
                          </div>}
                      </>}
                  </Card>
                </div>
              </div>
            </>}
        </div>
      </div>

      <style>{`
        @media (max-width: 760px) {
          .sa-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>;
}